import { Link } from "react-router-dom";
import { MessageSquareQuote, Mic2, Volume2, Activity, Gauge, Waves } from "lucide-react";
import { CategoryBadge } from "./CategoryBadge";

const unquote = (s) => (s || "").replace(/^["“„'\s]+|["”'\s]+$/g, "");
const uniqueFonti = (fonti = []) => fonti.filter((f, i, a) => a.findIndex((x) => x.categoria === f.categoria) === i);

export const ImprovableMoments = ({ items = [] }) => (
  <section data-testid="report-momenti-migliorabili" className="surface min-w-0 p-5 lg:col-span-2">
    <h2 className="mb-1 flex items-center gap-2 text-sm font-semibold"><MessageSquareQuote className="h-4 w-4 text-primary" /> Cosa avresti potuto dire</h2>
    <p className="mb-4 text-xs text-muted-foreground">I momenti chiave della call: cosa ha detto il cliente, la tua risposta e un'alternativa basata sui tuoi contenuti.</p>
    {items.length === 0 ? <p className="text-sm text-muted-foreground">Nessun momento critico individuato: ottimo.</p> : (
      <ol className="space-y-4">
        {items.map((m, i) => (
          <li key={i} data-testid={`moment-${i}`} className="grid gap-3 rounded-xl bg-background p-4 lg:grid-cols-3">
            <div className="min-w-0">
              <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Cliente</p>
              <p className="mt-1 text-sm italic">“{unquote(m.cliente)}”</p>
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-mono uppercase tracking-widest text-red-300/80">Hai detto</p>
              <p className="mt-1 text-sm text-gray-300">{unquote(m.hai_detto) || "—"}</p>
              {m.perche_migliorare && <p className="mt-2 text-xs text-muted-foreground">{m.perche_migliorare}</p>}
            </div>
            <div className="min-w-0 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3">
              <p className="text-[11px] font-mono uppercase tracking-widest text-emerald-300">Avresti potuto dire</p>
              <p data-testid={`moment-alternative-${i}`} className="mt-1 text-sm font-medium leading-relaxed">{m.avresti_potuto_dire || "Alternativa non disponibile."}</p>
              {m.perche_funziona && <p className="mt-2 text-xs text-emerald-200/70">{m.perche_funziona}</p>}
              {m.fonti?.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-1.5">{uniqueFonti(m.fonti).map((f) => <Link key={f.id} to={`/contenuti/${f.content_id}`}><CategoryBadge categoria={f.categoria} /></Link>)}</div>
              ) : m.avresti_potuto_dire ? <p className="mt-2 text-[10px] font-mono text-muted-foreground">best practice generale · non presente nella tua KB</p> : null}
            </div>
          </li>
        ))}
      </ol>
    )}
  </section>
);

const Meter = ({ icon: Icon, label, value, note, testId }) => (
  <div data-testid={testId} className="rounded-lg bg-background p-3">
    <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-muted-foreground"><Icon className="h-3.5 w-3.5" />{label}</div>
    <p className="mt-1 text-lg font-bold">{value ?? "n/d"}</p>
    {note && <p className="text-xs capitalize text-muted-foreground">{note}</p>}
  </div>
);

const STYLE_COLOR = { assertivo: "#34D399", empatico: "#34D399", neutro: "#9CA3AF", insicuro: "#F59E0B", prolisso: "#F59E0B", aggressivo: "#F87171" };

export const ToneAnalysis = ({ voce, tono }) => (
  <section data-testid="report-tono" className="surface min-w-0 p-5 lg:col-span-2">
    <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
      <h2 className="flex items-center gap-2 text-sm font-semibold"><Mic2 className="h-4 w-4 text-primary" /> Tono di voce e stile comunicativo</h2>
      {tono?.voce?.punteggio != null && <span data-testid="tone-score" className="rounded-full bg-background px-3 py-1 text-xs font-mono">Voce <b>{tono.voce.punteggio}/10</b></span>}
    </div>
    {voce?.disponibile ? (
      <>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Meter testId="tone-ritmo" icon={Gauge} label="Ritmo" value={voce.wpm ? `${voce.wpm} ppm` : null} note={voce.etichette?.ritmo} />
          <Meter testId="tone-volume" icon={Volume2} label="Volume" value={voce.volume_db != null ? `${voce.volume_db} dB` : null} note={voce.etichette?.volume} />
          <Meter testId="tone-intonazione" icon={Waves} label="Intonazione" value={voce.pitch_hz ? `${voce.pitch_hz} Hz` : null} note={voce.etichette?.intonazione || "non rilevata"} />
          <Meter testId="tone-esitazioni" icon={Activity} label="Riempitivi" value={`${voce.riempitivi_per_100}/100 parole`} note={voce.etichette?.esitazioni} />
        </div>
        {!voce.attribuito_a_venditore && <p className="mt-2 text-xs text-amber-300">Voce non attribuita: metriche calcolate su tutti i parlanti. Segna la tua voce durante la call per un'analisi precisa.</p>}
      </>
    ) : (
      <p className="text-xs text-muted-foreground">Dati vocali non disponibili per questa sessione (registrata prima dell'analisi audio).</p>
    )}
    {tono ? (
      <div className="mt-5 grid gap-4 lg:grid-cols-2">
        {tono.voce && (
          <div data-testid="tone-voice-block" className="space-y-2 text-sm">
            <p className="font-medium">{tono.voce.valutazione}</p>
            <ul className="space-y-1 text-xs text-muted-foreground">
              {["ritmo", "volume", "intonazione", "esitazioni"].map((k) => tono.voce[k] && <li key={k}><span className="font-mono uppercase text-gray-400">{k}:</span> {tono.voce[k]}</li>)}
            </ul>
          </div>
        )}
        {tono.tono_comunicativo && (
          <div data-testid="tone-style-block" className="space-y-2 text-sm">
            <p className="flex items-center gap-2">Stile: <span className="rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize" style={{ background: `${STYLE_COLOR[tono.tono_comunicativo.stile] || "#9CA3AF"}22`, color: STYLE_COLOR[tono.tono_comunicativo.stile] || "#9CA3AF" }}>{tono.tono_comunicativo.stile}</span></p>
            <p className="text-muted-foreground">{tono.tono_comunicativo.commento}</p>
            {(tono.tono_comunicativo.esempi || []).slice(0, 3).map((e, i) => (
              <p key={i} className="rounded-lg bg-background p-2 text-xs"><span className="italic">“{unquote(e.frase)}”</span> <span className="text-muted-foreground">→ {e.effetto}</span></p>
            ))}
          </div>
        )}
        {tono.consigli?.length > 0 && (
          <div data-testid="tone-tips" className="lg:col-span-2">
            <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Da allenare</p>
            <ul className="mt-1 space-y-1 text-sm">{tono.consigli.map((c, i) => <li key={i} className="flex gap-2"><span className="text-primary">•</span>{c}</li>)}</ul>
          </div>
        )}
      </div>
    ) : null}
  </section>
);

export const SegmentChips = ({ m }) => {
  if (!m) return null;
  return (
    <span className="ml-auto flex shrink-0 flex-wrap gap-1 text-[10px] font-mono text-muted-foreground">
      {m.wpm && <span className="rounded bg-background px-1.5 py-0.5">{m.wpm} ppm</span>}
      {m.riempitivi > 0 && <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-amber-300">{m.riempitivi} riemp.</span>}
      {m.interrotto && <span className="rounded bg-red-500/15 px-1.5 py-0.5 text-red-300">interruzione</span>}
    </span>
  );
};
